// Role options
export const roles = ['Duelist', 'Initiator', 'Controller', 'Sentinel', 'Flex'];

// Always get images from public folder, regardless of route or navigation
const PUBLIC_URL = process.env.PUBLIC_URL || '';

export const roleImages = {
  controller: `${PUBLIC_URL}/images/roles/controller.png`,
  duelist: `${PUBLIC_URL}/images/roles/duelist.png`,
  initiator: `${PUBLIC_URL}/images/roles/initiator.png`,
  sentinel: `${PUBLIC_URL}/images/roles/sentinel.png`,
  flex: `${PUBLIC_URL}/images/roles/flex.png`,
};

export const rankImages = {
  radiant: `${PUBLIC_URL}/images/ranks/radiant.png`,

  // Immortal (3 tiers)
  immortal1: `${PUBLIC_URL}/images/ranks/immortal_1.png`,
  immortal2: `${PUBLIC_URL}/images/ranks/immortal_2.png`,
  immortal3: `${PUBLIC_URL}/images/ranks/immortal_3.png`,

  // Ascendant (3 tiers)
  ascendant1: `${PUBLIC_URL}/images/ranks/ascendant_1.png`,
  ascendant2: `${PUBLIC_URL}/images/ranks/ascendant_2.png`,
  ascendant3: `${PUBLIC_URL}/images/ranks/ascendant_3.png`,

  // Diamond (3 tiers)
  diamond1: `${PUBLIC_URL}/images/ranks/diamond_1.png`,
  diamond2: `${PUBLIC_URL}/images/ranks/diamond_2.png`,
  diamond3: `${PUBLIC_URL}/images/ranks/diamond_3.png`,

  // Platinum (3 tiers)
  platinum1: `${PUBLIC_URL}/images/ranks/platinum_1.png`,
  platinum2: `${PUBLIC_URL}/images/ranks/platinum_2.png`,
  platinum3: `${PUBLIC_URL}/images/ranks/platinum_3.png`,

  // Gold (3 tiers)
  gold1: `${PUBLIC_URL}/images/ranks/gold_1.png`,
  gold2: `${PUBLIC_URL}/images/ranks/gold_2.png`,
  gold3: `${PUBLIC_URL}/images/ranks/gold_3.png`,

  // Silver (3 tiers)
  silver1: `${PUBLIC_URL}/images/ranks/silver_1.png`,
  silver2: `${PUBLIC_URL}/images/ranks/silver_2.png`,
  silver3: `${PUBLIC_URL}/images/ranks/silver_3.png`,

  // Bronze (3 tiers)
  bronze1: `${PUBLIC_URL}/images/ranks/bronze_1.png`,
  bronze2: `${PUBLIC_URL}/images/ranks/bronze_2.png`,
  bronze3: `${PUBLIC_URL}/images/ranks/bronze_3.png`,

  // Iron (3 tiers)
  iron1: `${PUBLIC_URL}/images/ranks/iron_1.png`,
  iron2: `${PUBLIC_URL}/images/ranks/iron_2.png`,
  iron3: `${PUBLIC_URL}/images/ranks/iron_3.png`,
};

// Helper function to get rank image
export const getRankImage = (rank) => {
  if (!rank) return rankImages.iron1;

  const rankLower = rank.toLowerCase().replace(/\s+/g, '');

  // Radiant (no tiers)
  if (rankLower.includes('radiant')) return rankImages.radiant;

  // Immortal (1, 2, 3)
  if (rankLower.includes('immortal')) {
    if (rankLower.includes('1')) return rankImages.immortal1;
    if (rankLower.includes('2')) return rankImages.immortal2;
    if (rankLower.includes('3')) return rankImages.immortal3;
    return rankImages.immortal1; // default to tier 1
  }

  // Ascendant (1, 2, 3)
  if (rankLower.includes('ascendant')) {
    if (rankLower.includes('1')) return rankImages.ascendant1;
    if (rankLower.includes('2')) return rankImages.ascendant2;
    if (rankLower.includes('3')) return rankImages.ascendant3;
    return rankImages.ascendant1;
  }

  // Diamond (1, 2, 3)
  if (rankLower.includes('diamond')) {
    if (rankLower.includes('1')) return rankImages.diamond1;
    if (rankLower.includes('2')) return rankImages.diamond2;
    if (rankLower.includes('3')) return rankImages.diamond3;
    return rankImages.diamond1;
  }

  // Platinum (1, 2, 3)
  if (rankLower.includes('platinum')) {
    if (rankLower.includes('1')) return rankImages.platinum1;
    if (rankLower.includes('2')) return rankImages.platinum2;
    if (rankLower.includes('3')) return rankImages.platinum3;
    return rankImages.platinum1;
  }

  // Gold (1, 2, 3)
  if (rankLower.includes('gold')) {
    if (rankLower.includes('1')) return rankImages.gold1;
    if (rankLower.includes('2')) return rankImages.gold2;
    if (rankLower.includes('3')) return rankImages.gold3;
    return rankImages.gold1;
  }

  // Silver (1, 2, 3)
  if (rankLower.includes('silver')) {
    if (rankLower.includes('1')) return rankImages.silver1;
    if (rankLower.includes('2')) return rankImages.silver2;
    if (rankLower.includes('3')) return rankImages.silver3;
    return rankImages.silver1;
  }

  // Bronze (1, 2, 3)
  if (rankLower.includes('bronze')) {
    if (rankLower.includes('1')) return rankImages.bronze1;
    if (rankLower.includes('2')) return rankImages.bronze2;
    if (rankLower.includes('3')) return rankImages.bronze3;
    return rankImages.bronze1;
  }

  // Iron (1, 2, 3)
  if (rankLower.includes('iron')) {
    if (rankLower.includes('1')) return rankImages.iron1;
    if (rankLower.includes('2')) return rankImages.iron2;
    if (rankLower.includes('3')) return rankImages.iron3;
    return rankImages.iron1;
  }

  // Default fallback
  return rankImages.iron1;
};

// Helper function to get role image
export const getRoleImage = (role) => {
  if (!role) return null;
  const roleLower = role.toLowerCase();
  if (roleLower.includes('controller')) return roleImages.controller;
  if (roleLower.includes('duelist')) return roleImages.duelist;
  if (roleLower.includes('initiator')) return roleImages.initiator;
  if (roleLower.includes('sentinel')) return roleImages.sentinel;
  if (roleLower.includes('flex')) return roleImages.flex;
  return null;
};

// Helper function to get rank numerical value for sorting (higher number = higher rank)
export const getRankValue = (rankImagePath) => {
  if (!rankImagePath) return 0;

  // Extract rank name from image path
  const rankName = rankImagePath.toLowerCase();

  // Radiant (highest - 100)
  if (rankName.includes('radiant')) return 100;

  // Immortal (90-92)
  if (rankName.includes('immortal')) {
    if (rankName.includes('3')) return 92;
    if (rankName.includes('2')) return 91;
    if (rankName.includes('1')) return 90;
    return 90; // default
  }

  // Ascendant (80-82)
  if (rankName.includes('ascendant')) {
    if (rankName.includes('3')) return 82;
    if (rankName.includes('2')) return 81;
    if (rankName.includes('1')) return 80;
    return 80;
  }

  // Diamond (70-72)
  if (rankName.includes('diamond')) {
    if (rankName.includes('3')) return 72;
    if (rankName.includes('2')) return 71;
    if (rankName.includes('1')) return 70;
    return 70;
  }

  // Platinum (60-62)
  if (rankName.includes('platinum')) {
    if (rankName.includes('3')) return 62;
    if (rankName.includes('2')) return 61;
    if (rankName.includes('1')) return 60;
    return 60;
  }

  // Gold (50-52)
  if (rankName.includes('gold')) {
    if (rankName.includes('3')) return 52;
    if (rankName.includes('2')) return 51;
    if (rankName.includes('1')) return 50;
    return 50;
  }

  // Silver (40-42)
  if (rankName.includes('silver')) {
    if (rankName.includes('3')) return 42;
    if (rankName.includes('2')) return 41;
    if (rankName.includes('1')) return 40;
    return 40;
  }

  // Bronze (30-32)
  if (rankName.includes('bronze')) {
    if (rankName.includes('3')) return 32;
    if (rankName.includes('2')) return 31;
    if (rankName.includes('1')) return 30;
    return 30;
  }

  // Iron (20-22)
  if (rankName.includes('iron')) {
    if (rankName.includes('3')) return 22;
    if (rankName.includes('2')) return 21;
    if (rankName.includes('1')) return 20;
    return 20;
  }

  // Default (lowest)
  return 0;
};
